import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { setPageMeta } from '../utils/seo';
import {
  Megaphone, ArrowRight, House, BookOpen, Wrench, Briefcase,
  Target, ShieldCheck, ChartLineUp, UploadSimple, CreditCard, RocketLaunch, CaretDown,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEMENT_ICONS = { homepage: House, guides: BookOpen, hire: Wrench, jobs: Briefcase };
const PLACEMENT_BLURBS = {
  homepage: 'Prime real estate below our hero — the first thing every visitor sees.',
  guides: 'Inside our 2026 cost & hiring guides — readers actively budgeting projects.',
  hire: 'On 250+ trade & city pages where homeowners pick their next pro.',
  jobs: 'Above the job board pros check daily for their next paycheck.',
};

const Faq = ({ f, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-2 border-neutral-900 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left" data-testid={`adv-faq-${i}`}>
        <span className="font-black">{f.q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-4 pb-4 text-neutral-600 text-sm leading-relaxed">{f.a}</p>}
    </div>
  );
};

const FAQS = [
  { q: 'Who sees my ad?', a: 'Homeowners actively planning and budgeting construction projects, plus contractors and skilled workers using ConstructHelp daily to find work, price jobs, and manage crews. High intent, zero wasted eyeballs.' },
  { q: 'What are the banner specs?', a: '1200×200px works best (wide banner). JPG, PNG, or WebP under 500KB. A clear logo, one message, and one call to action outperform busy designs.' },
  { q: 'How fast does my ad go live?', a: 'Pay by card, then our team reviews your banner — usually within 24 hours. It goes live the moment it is approved and runs for the full duration you purchased.' },
  { q: 'Can I run multiple placements?', a: 'Yes — many advertisers pair the Homepage Banner with Guide pages to catch visitors at both ends of their research. Each placement is booked separately in about two minutes.' },
  { q: 'What kinds of companies advertise?', a: 'Building material suppliers, tool and equipment brands, insurance and warranty providers, project financing, dumpster and equipment rental, and local service businesses.' },
];

export default function AdvertisingPromo() {
  const [placements, setPlacements] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta({
      title: 'Advertise to Homeowners & Contractors — Ad Space on ConstructHelp',
      description: 'Reach homeowners planning construction projects and the pros who build them. Banner placements from $39/week with transparent impressions and click tracking.',
      url: `${window.location.origin}/advertising`,
    });
    axios.get(`${API}/api/ads/placements`).then((r) => setPlacements(r.data.placements)).catch(() => {});
    axios.get(`${API}/api/trade-stats`, { params: { terms: 'construction,roofing,plumbing,electrical,remodel' } })
      .then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#E8E9EB]" data-testid="advertising-promo-page">
      {/* Hero */}
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-6xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#D95800] text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 border-2 border-white mb-5">
              <Megaphone size={14} weight="fill" /> Ad Space Available
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-tight mb-5">
              Your Brand, In Front of People Who Are Building
            </h1>
            <p className="text-neutral-300 text-lg leading-relaxed mb-8">
              ConstructHelp visitors aren't browsing — they're budgeting remodels, hiring roofers,
              and buying materials this week. Put your banner where the decisions happen.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/advertise" className="h-13 px-8 py-3 flex items-center gap-2 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-white shadow-[4px_4px_0px_rgba(255,255,255,0.25)]" data-testid="promo-cta-hero">
                Book Your Spot <ArrowRight size={18} weight="bold" />
              </Link>
              <a href="#placements" className="h-13 px-8 py-3 flex items-center bg-transparent text-white font-black uppercase border-2 border-white hover:bg-white/10" data-testid="promo-see-placements">
                See Placements
              </a>
            </div>
          </div>
          <img src="/advertising-hero.jpg" alt="Marketing team reviewing an ad placement on ConstructHelp"
            className="hidden lg:block w-full aspect-[3/2] object-cover border-2 border-white shadow-[8px_8px_0px_#D95800]" />
        </div>
      </div>

      {/* Audience stats */}
      <div className="bg-white border-b-2 border-neutral-900">
        <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center" data-testid="promo-stats">
          {[
            { n: stats ? `${stats.total_pros}+` : '—', l: 'Verified pros on the platform' },
            { n: '340+', l: 'SEO pages pulling in project planners' },
            { n: '12', l: 'Trades, from roofing to remodeling' },
            { n: '100%', l: 'High-intent construction audience' },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-3xl sm:text-4xl font-black text-[#D95800]">{s.n}</p>
              <p className="text-xs font-bold uppercase text-neutral-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-14 space-y-16">
        {/* Why advertise */}
        <div>
          <h2 className="text-2xl font-black uppercase mb-6 text-center">Why brands choose ConstructHelp</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Target, t: 'Zero wasted reach', d: 'Every visitor is a homeowner planning a project or a pro who builds for a living. If you sell to construction, this is your audience — all of it.' },
              { icon: ChartLineUp, t: 'Transparent results', d: 'Impressions and clicks are tracked on every ad. No black-box metrics — you see exactly what your placement delivers.' },
              { icon: ShieldCheck, t: 'Brand-safe by design', d: 'One sponsor banner per page, clearly labeled, reviewed by a human before going live. Your brand never competes with clutter.' },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={i} className="bg-white border-2 border-neutral-900 p-6 shadow-[4px_4px_0px_#000]">
                <div className="w-12 h-12 bg-[#D95800] border-2 border-black flex items-center justify-center mb-4">
                  <Icon size={26} weight="bold" className="text-white" />
                </div>
                <h3 className="font-black uppercase mb-2">{t}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Placements */}
        <div id="placements">
          <h2 className="text-2xl font-black uppercase mb-2 text-center">Pick your placement</h2>
          <p className="text-center text-neutral-500 mb-8">Flat weekly pricing. No auctions, no bidding wars, no surprises.</p>
          <div className="grid sm:grid-cols-2 gap-5" data-testid="promo-placements">
            {placements.map((p) => {
              const Icon = PLACEMENT_ICONS[p.id] || Megaphone;
              return (
                <div key={p.id} className="bg-white border-2 border-neutral-900 p-6 shadow-[4px_4px_0px_#000] flex gap-4" data-testid={`promo-placement-${p.id}`}>
                  <div className="w-12 h-12 shrink-0 bg-neutral-900 border-2 border-black flex items-center justify-center">
                    <Icon size={24} weight="bold" className="text-[#D95800]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="font-black uppercase">{p.label}</h3>
                      <span className="font-black text-[#D95800] whitespace-nowrap text-right">
                        {p.regular_weekly_price && (
                          <span className="text-neutral-400 line-through font-bold text-sm mr-1.5">${p.regular_weekly_price}</span>
                        )}
                        ${p.weekly_price}/wk
                        {p.regular_weekly_price && (
                          <span className="block text-[9px] font-black uppercase tracking-wider text-emerald-700" data-testid={`promo-intro-badge-${p.id}`}>Intro price</span>
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600 mt-1">{PLACEMENT_BLURBS[p.id]}</p>
                    <img src={`/ad-previews/${p.id}.jpg`} alt={`Screenshot of the ${p.label} ad placement in context`}
                      className="w-full border-2 border-neutral-900 mt-3" loading="lazy" data-testid={`promo-preview-${p.id}`} />
                    <p className="text-[10px] font-bold text-neutral-400 mt-1 uppercase">Green box = your banner spot</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* How it works */}
        <div className="bg-neutral-900 border-2 border-black p-10 shadow-[6px_6px_0px_#D95800]">
          <h2 className="text-2xl font-black uppercase text-white mb-8 text-center">Live in 3 steps</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: UploadSimple, t: '1. Upload your banner', d: '1200×200 JPG or PNG. Pick your placement and how many weeks to run.' },
              { icon: CreditCard, t: '2. Pay securely', d: 'Card checkout via Stripe. Your total is shown upfront — no contracts, no minimums.' },
              { icon: RocketLaunch, t: '3. Go live in 24h', d: 'A human reviews your ad, then it runs for your full booking with tracking included.' },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 mx-auto bg-[#D95800] border-2 border-white flex items-center justify-center mb-4">
                  <Icon size={28} weight="bold" className="text-white" />
                </div>
                <h3 className="font-black uppercase text-white mb-2">{t}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-black uppercase mb-6 text-center">Advertiser FAQs</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => <Faq key={i} f={f} i={i} />)}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-[#D95800] border-2 border-neutral-900 p-10 text-center shadow-[6px_6px_0px_#000]">
          <h2 className="text-3xl font-black uppercase text-white mb-3">Spots are one-per-page — claim yours</h2>
          <p className="text-orange-100 mb-6 max-w-xl mx-auto">Book in two minutes. Your banner can be in front of project-ready homeowners by this time tomorrow.</p>
          <Link to="/advertise" className="inline-flex h-13 px-10 py-3 items-center gap-2 bg-neutral-900 text-white font-black uppercase border-2 border-white" data-testid="promo-cta-bottom">
            Book Your Ad Spot <ArrowRight size={18} weight="bold" />
          </Link>
          <p className="text-xs text-orange-200 mt-4">Questions first? <Link to="/contact" className="underline font-bold">Talk to us</Link> — messages are tagged “sales” and answered fast.</p>
        </div>
      </div>
    </div>
  );
}
